import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "spark-core-dx/components";
import DestinationScheduleControl from "../../destinationSchedule/DestinationScheduleControl";
import {
  SCHEDULE_DECISION_CONFIGURE,
  SCHEDULE_DECISION_NONE
} from "../../destinationSchedule/destinationScheduleControlUtils";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  getDestinationDeploymentRows,
  getDestinationIdFromValues,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationSetupDefaults,
  getEntityDeploymentRows,
  getEntityObjectDisplayName,
  getSavedEntityDeploymentStepValues,
  hasLoadedManagerMetadata,
  isCoreProfileMetadataReady,
  loadDestinationById,
  loadDestinationManagerMetadata
} from "./destinationSetupStepUtils";
import {
  getId,
  getOperationItems,
  readValue,
  requireServiceResponse,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const DestinationScheduleConfiguratorStep = ({
  context,
  onSave,
  onContinue,
  registerStepController,
  onDirty
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults),
    [context.productResourceDefaults, wizard]
  );
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const destinationMgr = resourceDomainContext?.managers?.[managerKey];
  const scheduleMgr = resourceDomainContext?.managers?.destinationSchedule;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const savedEntityValues = getSavedEntityDeploymentStepValues(wizard, values);
  const destinationId = getDestinationIdFromValues(wizard, savedEntityValues, productResourceDefaults);
  const destinationLabel = getDestinationLabel(productResourceDefaults);
  const resourceConfiguration = readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  const clientResourceId = readValue(wizard, "ClientResourceId", "clientResourceId") || readValue(resourceConfiguration, "ClientResourceId", "clientResourceId") || "";
  const resourceConfigurationId = readValue(wizard, "ResourceConfigurationId", "resourceConfigurationId") || getId(resourceConfiguration);
  const profileMetadataReady = isCoreProfileMetadataReady(coreContext);
  const [destination, setDestination] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [entityDeployments, setEntityDeployments] = useState([]);
  const [entityObjects, setEntityObjects] = useState([]);
  const [loading, setLoading] = useState(Boolean(destinationId));
  const [localError, setLocalError] = useState("");
  const controlApiRef = useRef(null);
  const decisionRef = useRef(readValue(values, "ScheduleDecision", "scheduleDecision") || SCHEDULE_DECISION_NONE);
  const destinationMgrRef = useRef(destinationMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const coreContextRef = useRef(coreContext);
  const coreStateRef = useRef(coreState);
  const loadKeyRef = useRef("");

  destinationMgrRef.current = destinationMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  coreContextRef.current = coreContext;
  coreStateRef.current = coreState;

  useEffect(() => {
    let mounted = true;
    const activeDestinationMgr = destinationMgrRef.current;
    const activeEntityObjectMgr = entityObjectMgrRef.current;
    const loadKey = `${managerKey}:${destinationId || "none"}:destination-schedules`;
    if (!destinationId) {
      setDestination(null);
      setDeployments([]);
      setEntityDeployments([]);
      setEntityObjects([]);
      setLoading(false);
      setLocalError("");
      return undefined;
    }
    if (!activeDestinationMgr) {
      setLoading(false);
      setLocalError(`${destinationLabel} service is not available.`);
      return undefined;
    }
    if (!activeEntityObjectMgr?.Query) {
      setLoading(false);
      setLocalError("Entity Object service is not available.");
      return undefined;
    }
    if (!hasLoadedManagerMetadata(activeDestinationMgr) && !isCoreProfileMetadataReady(coreContextRef.current)) {
      setLoading(true);
      setLocalError("");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        const [loadedDestination, metadataResult] = await Promise.all([
          loadDestinationById({
            manager: activeDestinationMgr,
            destinationId,
            coreState: coreStateRef.current,
            fallbackMessage: `${destinationLabel} could not be loaded.`
          }),
          loadDestinationManagerMetadata({
            manager: activeDestinationMgr,
            coreContext: coreContextRef.current
          })
        ]);
        if (!loadedDestination) throw new Error(`The linked ${destinationLabel} could not be loaded.`);
        requireServiceResponse(metadataResult, "Destination metadata could not be loaded.");
        const activeSchemaVersionId = readValue(loadedDestination, "ActiveSchemaVersionId", "activeSchemaVersionId") || "";
        const entityObjectResult = activeSchemaVersionId
          ? await activeEntityObjectMgr.Query({
            model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
            params: { schemaVersionId: activeSchemaVersionId }
          })
          : { Items: [] };
        if (!mounted) return;
        requireSuccessfulServiceResponse(entityObjectResult, "Entity Objects could not be loaded.", coreStateRef.current);
        setDestination(loadedDestination);
        setDeployments(getDestinationDeploymentRows(loadedDestination, productResourceDefaults));
        setEntityDeployments(getEntityDeploymentRows(loadedDestination));
        setEntityObjects(getOperationItems(entityObjectResult)
          .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
          .sort((left, right) => getEntityObjectDisplayName(left).localeCompare(getEntityObjectDisplayName(right))));
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || "Destination schedules could not be loaded.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [destinationId, destinationLabel, managerKey, productResourceDefaults, profileMetadataReady]);

  const prepareScheduleValues = useCallback(async () => {
    if (!controlApiRef.current?.saveSchedules) {
      return {
        ScheduleDecision: decisionRef.current || SCHEDULE_DECISION_NONE,
        DestinationScheduleIds: [],
        DestinationScheduleCount: 0,
        EnabledDestinationScheduleCount: 0
      };
    }
    return controlApiRef.current.saveSchedules(decisionRef.current || SCHEDULE_DECISION_NONE);
  }, []);

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      prepareSave: prepareScheduleValues,
      prepareContinue: prepareScheduleValues
    });
    return () => registerStepController?.(null);
  }, [prepareScheduleValues, registerStepController]);

  const handleSetupSave = useCallback(() => {
    decisionRef.current = SCHEDULE_DECISION_CONFIGURE;
    onSave?.();
  }, [onSave]);

  const handleSetupContinue = useCallback(() => {
    decisionRef.current = SCHEDULE_DECISION_CONFIGURE;
    onContinue?.();
  }, [onContinue]);

  const handleNoSchedule = useCallback(() => {
    decisionRef.current = SCHEDULE_DECISION_NONE;
    onContinue?.();
  }, [onContinue]);

  const countText = deployments.length
    ? `${deployments.length} deployment${deployments.length === 1 ? "" : "s"}`
    : "";

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title="Schedule"
        description="Save optional schedule templates for this destination. Setup mode does not apply, restart, preview, or run schedules."
        countText={countText}
      />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : (
        <DestinationScheduleControl
          mode="setup"
          destination={destination}
          deployments={deployments}
          entityObjects={entityObjects}
          entityDeployments={entityDeployments}
          defaults={productResourceDefaults}
          clientResourceId={clientResourceId}
          resourceConfigurationId={resourceConfigurationId}
          scheduleManager={scheduleMgr}
          readOnly={readOnly}
          saving={saving}
          onDirty={onDirty}
          onApiReady={(api) => {
            controlApiRef.current = api;
          }}
          onSetupSave={handleSetupSave}
          onSetupContinue={handleSetupContinue}
          onSetupNoSchedule={handleNoSchedule}
        />
      )}
    </div>
  );
};

export default DestinationScheduleConfiguratorStep;
